import { RxCollection, RxDocument } from 'rxdb'
import { DateTime } from 'luxon'
import { BaseSchema } from './BaseSchema'
import { ModelService, Store } from '../services'
import { IS_OFFLINE_ID } from './crud'
import { IModelSchemaConfig } from './schema'

export interface ISyncItem {
  name: string
  doc: RxDocument<any>
  model: ModelService<any>
}

/**
 * Get all the models that are tagged syncable
 * @param store 
 */
export const GET_SYNCABLE_MODELS = (store: Store) => {
  return Array.from(store.models.values())
    .reduce((acc: ModelService<any>[], service) => {
      const { CONFIG } = service.schema
      if (CONFIG.sync !== false && CONFIG?.properties?.meta) {
        acc.push(service)
      }
      return acc
    }, [])
}

/**
 * Returns only entities that have changes not persisted to the API
 * @param c A collection to get unsynced entities from
 */
export const GET_UNSYNCED_ENTITIES = async (c: RxCollection) => {
  const items: RxDocument<BaseSchema>[] = await c.find({
    selector: {
      $or: [
        { 'meta.uuid': { $exists: false } },
        { 'meta.created_offline_at': { $exists: true } },
        { 'meta.updated_offline_at': { $exists: true } },
        { 'meta.deleted_offline_at': { $exists: true } },
      ],
    },
  }).exec()

  return items.filter(item => {
    if (item.meta?.deleted_offline_at) { return true }
    if (item.meta?.last_synced_at) {
      const { created_offline_at, updated_offline_at } = item.meta
      return item.meta.last_synced_at < (created_offline_at || 0) || item.meta.last_synced_at < (updated_offline_at || 0)
    } else {
      return true
    }
  })
}

// TODO Escape etc properly
export const PARAMS_TO_QUERY_STRING = (params: any) => Object.entries(params).map(([key, value]) => `${key}=${value}`).join('&')

/**
 * Merge in changes into a doc, recursively.
 * @param doc The current state of the doc
 * @param changes Any desired changes in the new version
 */
export const MERGE_IN_DOC_CHANGES = (doc: any, changes: any) => {
  Object.entries(changes).forEach(([key, value]) => {
    if (key === 'id') { return }
    if (key === 'meta') { return }
    // console.log('merging change', `${key}:`, value, doc[key])
    if (doc[key]) {
      // TODO If the doc has unsynced changes, conflict resolve
      if (typeof doc[key] === 'object' && typeof value === 'object') {
        doc[key] = MERGE_IN_DOC_CHANGES(doc[key], value)
        return
      }
    }

    doc[key] = value
  })
  return doc
}

/**
 * Cleans up an RxDocument and returns an object ready to save to the API
 * @param rxDoc 
 */
export const PREPARE_DOC_FOR_SYNC = (rxDoc: RxDocument<BaseSchema>, config: IModelSchemaConfig) => {
  const doc: Partial<BaseSchema> = rxDoc.toJSON()
  if (doc.id) {
    if (IS_OFFLINE_ID(doc.id)) {
      if (doc.meta && doc.meta.uuid) {
        doc.id = doc.meta.uuid
      } else {
        delete doc.id
      }
    }
  }
  // TODO Loop over associations to make sure IDs are valid
  if (doc.meta) {
    if (doc.meta.deleted_offline_at) { doc.deleted_at = DateTime.fromMillis(doc.meta.deleted_offline_at).toISO() }
  }

  if (config.local && config.local.length) {
    config.local.forEach(key => {
      delete (doc as any)[key]
    })
  }

  delete doc.meta
  return doc
}

export const GENERATE_SYNCED_RAW = (raw: any) => {
  return {
    uuid: raw.id,
    raw: JSON.parse(JSON.stringify(raw)),
    fresh: true,
  }
}
